import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ExternalLink, RefreshCw, CheckCircle2, XCircle, AlertTriangle, Save, Radio, Copy, Info } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

const STATUS_META = {
  index:    { label: "Indexable", color: "bg-emerald-100 text-emerald-700 border-emerald-200", icon: CheckCircle2 },
  noindex:  { label: "Noindex",   color: "bg-orange-100 text-orange-700 border-orange-200",    icon: AlertTriangle },
  draft:    { label: "Brouillon", color: "bg-gray-100 text-gray-700 border-gray-200",          icon: XCircle },
};

const TYPE_LABEL = { region: "Région", department: "Département", city: "Ville" };

export default function AdminSeo() {
  const { logout } = useAuth();
  const [mainTab, setMainTab] = useState("pages"); // "pages" | "submit"
  const [tab, setTab] = useState("region");
  const [data, setData] = useState({ regions: [], departments: [], cities: [] });
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null); // slug_path
  const [detail, setDetail] = useState(null);     // { page, override }
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ title: "", meta_description: "", h1: "", body: "", faq: "", force_status: "" });

  // Onglet "Soumission moteurs"
  const [config, setConfig] = useState(null);
  const [configForm, setConfigForm] = useState({ google_site_verification: "", bing_verification: "", indexnow_key: "" });
  const [savingConfig, setSavingConfig] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState(null);
  const [history, setHistory] = useState([]);

  const loadConfig = () => {
    api.get("/admin/seo/config").then((r) => {
      setConfig(r.data);
      setConfigForm({
        google_site_verification: r.data.google_site_verification || "",
        bing_verification: r.data.bing_verification || "",
        indexnow_key: r.data.indexnow_key || "",
      });
    });
    api.get("/admin/seo/indexnow-history").then((r) => setHistory(r.data || []));
  };

  const saveConfig = async () => {
    setSavingConfig(true);
    try {
      const r = await api.put("/admin/seo/config", configForm);
      setConfig(r.data);
    } finally { setSavingConfig(false); }
  };

  const [cronStatus, setCronStatus] = useState(null);

  const loadCronStatus = async () => {
    try {
      const r = await api.get("/admin/seo/indexnow-cron-status");
      setCronStatus(r.data);
    } catch { setCronStatus(null); }
  };

  const triggerCron = async () => {
    if (!window.confirm("Déclencher maintenant la soumission IndexNow (scope=all — géo + intercité + blog) ?")) return;
    setSubmitting(true);
    setSubmitResult(null);
    try {
      const r = await api.post("/admin/seo/trigger-indexnow-cron");
      setSubmitResult(r.data);
      loadConfig();
      loadCronStatus();
    } catch (err) {
      setSubmitResult({ ok: false, error: err.response?.data?.detail || "Erreur" });
    } finally { setSubmitting(false); }
  };

  const submitIndexNow = async (scope = "geo") => {
    const scopeLabels = {
      geo: `${stats.index} URLs géo (régions/départements/villes)`,
      intercity: "623 URLs intercités",
      blog: "URLs du blog",
      all: `Toutes les URLs (géo + intercités + blog)`,
    };
    if (!window.confirm(`Soumettre : ${scopeLabels[scope] || scope} à Bing + Yandex + IndexNow ?`)) return;
    setSubmitting(true);
    setSubmitResult(null);
    try {
      const r = await api.post("/admin/seo/submit-indexnow", { scope });
      setSubmitResult(r.data);
      loadConfig();
    } catch (err) {
      setSubmitResult({ ok: false, error: err.response?.data?.detail || "Erreur" });
    } finally { setSubmitting(false); }
  };

  const load = () => {
    setLoading(true);
    api.get("/admin/seo/pages").then((r) => setData(r.data)).finally(() => setLoading(false));
  };

  useEffect(() => { load(); loadConfig(); loadCronStatus(); document.title = "SEO — Admin"; }, []);

  const openDetail = async (slug_path) => {
    setSelected(slug_path);
    const r = await api.get("/admin/seo/pages/detail", { params: { slug_path } });
    setDetail(r.data);
    const ov = r.data.override || {};
    setForm({
      title: ov.title || r.data.page.title,
      meta_description: ov.meta_description || r.data.page.meta_description,
      h1: ov.h1 || r.data.page.h1,
      body: (ov.body_paragraphs || r.data.page.body_paragraphs || []).join("\n\n"),
      faq: JSON.stringify(ov.faq || r.data.page.faq || [], null, 2),
      force_status: ov.force_status || "",
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      const orig = detail.page;
      if (form.title !== orig.title) payload.title = form.title;
      if (form.meta_description !== orig.meta_description) payload.meta_description = form.meta_description;
      if (form.h1 !== orig.h1) payload.h1 = form.h1;
      const bodyArr = form.body.split(/\n\n+/).map((s) => s.trim()).filter(Boolean);
      if (JSON.stringify(bodyArr) !== JSON.stringify(orig.body_paragraphs)) payload.body_paragraphs = bodyArr;
      try {
        const faqParsed = JSON.parse(form.faq);
        if (JSON.stringify(faqParsed) !== JSON.stringify(orig.faq)) payload.faq = faqParsed;
      } catch (_e) { /* invalid JSON, ignore */ }
      if (form.force_status) payload.force_status = form.force_status;

      await api.put("/admin/seo/pages", payload, { params: { slug_path: selected } });
      await openDetail(selected);
      load();
    } finally { setSaving(false); }
  };

  const resetOverride = async () => {
    if (!window.confirm("Supprimer les surcharges de cette page ?")) return;
    await api.delete("/admin/seo/pages", { params: { slug_path: selected } });
    await openDetail(selected);
    load();
  };

  const items = tab === "region" ? data.regions : tab === "department" ? data.departments : data.cities;

  const stats = {
    total: data.regions.length + data.departments.length + data.cities.length,
    index: [...data.regions, ...data.departments, ...data.cities].filter((p) => p.status === "index").length,
    noindex: [...data.regions, ...data.departments, ...data.cities].filter((p) => p.status === "noindex").length,
  };

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      <header className="bg-white border-b border-[#1C1C1C]">
        <div className="container mx-auto px-6 md:px-12 py-5 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <span className="eyebrow bg-[#1D3557] text-white px-2 py-1">Admin</span>
            <h1 className="font-heading font-black text-xl tracking-tight">SEO Landing Pages</h1>
          </div>
          <div className="flex items-center gap-4">
            <button onClick={load} className="text-sm font-semibold text-[#2C3E50] hover:text-[#2BD391]" data-testid="reload-seo">
              <RefreshCw className={`w-4 h-4 inline mr-1 ${loading ? "animate-spin" : ""}`} /> Recalculer
            </button>
            <Link to="/admin" className="text-sm font-semibold hover:text-[#E07A5F]" data-testid="back-admin"><ArrowLeft className="w-4 h-4 inline" /> Dashboard</Link>
            <button onClick={logout} className="text-sm font-semibold hover:text-[#C44536]">Déconnexion</button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-6 md:px-12 py-8">
        {/* Onglets principaux */}
        <div className="flex items-center gap-2 mb-6 border-b border-[#E5E7EB]">
          <button onClick={() => setMainTab("pages")} className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors ${mainTab === "pages" ? "border-[#2BD391] text-[#2C3E50]" : "border-transparent text-[#6B7280] hover:text-[#2C3E50]"}`} data-testid="maintab-pages">
            Pages SEO
          </button>
          <button onClick={() => setMainTab("submit")} className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors ${mainTab === "submit" ? "border-[#2BD391] text-[#2C3E50]" : "border-transparent text-[#6B7280] hover:text-[#2C3E50]"}`} data-testid="maintab-submit">
            Soumission moteurs
          </button>
        </div>

        {mainTab === "submit" ? (
          <SubmitPanel
            config={config}
            configForm={configForm}
            setConfigForm={setConfigForm}
            saveConfig={saveConfig}
            savingConfig={savingConfig}
            submitIndexNow={submitIndexNow}
            submitting={submitting}
            submitResult={submitResult}
            history={history}
            stats={stats}
            cronStatus={cronStatus}
            triggerCron={triggerCron}
          />
        ) : (<>
        {/* Stats globales */}
        <div className="grid grid-cols-3 gap-3 mb-6">
          <StatCard label="Total pages" value={stats.total} testid="stat-total" />
          <StatCard label="Indexables" value={stats.index} accent="emerald" testid="stat-index" />
          <StatCard label="Noindex" value={stats.noindex} accent="orange" testid="stat-noindex" />
        </div>

        <div className="flex items-center gap-2 mb-4 border-b border-[#E5E7EB]">
          {[
            ["region", "Régions", data.regions.length],
            ["department", "Départements", data.departments.length],
            ["city", "Villes", data.cities.length],
          ].map(([key, label, count]) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors ${tab === key ? "border-[#2BD391] text-[#2C3E50]" : "border-transparent text-[#6B7280] hover:text-[#2C3E50]"}`}
              data-testid={`tab-${key}`}
            >
              {label} ({count})
            </button>
          ))}
        </div>

        <div className="bg-white border border-[#1C1C1C] overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F2EFE9]">
              <tr className="text-left">
                <th className="px-4 py-3 text-xs uppercase tracking-widest font-bold">URL</th>
                <th className="px-4 py-3 text-xs uppercase tracking-widest font-bold">H1</th>
                <th className="px-4 py-3 text-xs uppercase tracking-widest font-bold">Score</th>
                <th className="px-4 py-3 text-xs uppercase tracking-widest font-bold">Mots</th>
                <th className="px-4 py-3 text-xs uppercase tracking-widest font-bold">Liens</th>
                <th className="px-4 py-3 text-xs uppercase tracking-widest font-bold">Statut</th>
                <th className="px-4 py-3 text-xs uppercase tracking-widest font-bold" />
              </tr>
            </thead>
            <tbody>
              {items.map((p) => {
                const M = STATUS_META[p.status] || STATUS_META.draft;
                return (
                  <tr key={p.slug_path} className="border-t border-[#E5E7EB] hover:bg-[#F7F9FB]" data-testid={`row-${p.slug_path}`}>
                    <td className="px-4 py-3 font-mono text-xs">
                      <a href={p.slug_path} target="_blank" rel="noreferrer" className="text-[#4285F4] hover:underline">
                        {p.slug_path} <ExternalLink className="w-3 h-3 inline" />
                      </a>
                    </td>
                    <td className="px-4 py-3 text-xs">{p.h1}</td>
                    <td className="px-4 py-3"><ScoreBadge score={p.score} /></td>
                    <td className={`px-4 py-3 text-xs font-mono ${p.word_count < 500 ? "text-orange-600 font-bold" : ""}`}>{p.word_count}</td>
                    <td className={`px-4 py-3 text-xs font-mono ${p.internal_links < 3 ? "text-orange-600 font-bold" : ""}`}>{p.internal_links}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border ${M.color}`}>
                        <M.icon className="w-3 h-3" /> {M.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button onClick={() => openDetail(p.slug_path)} className="text-xs uppercase tracking-widest hover:text-[#2BD391]" data-testid={`edit-${p.slug_path}`}>
                        Éditer
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        </>)}
      </div>

      {/* Modal éditeur */}
      {selected && detail && (
        <div className="fixed inset-0 bg-black/60 flex items-start justify-center z-50 p-4 overflow-y-auto" onClick={() => { setSelected(null); setDetail(null); }}>
          <div className="bg-white rounded-2xl max-w-4xl w-full my-8 p-8 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="seo-detail">
            <div className="flex items-center justify-between mb-6">
              <div>
                <div className="text-xs uppercase tracking-widest text-[#6B7280] mb-1">{TYPE_LABEL[detail.page.type]}</div>
                <h2 className="font-heading font-extrabold text-2xl text-[#2C3E50]">{detail.page.h1}</h2>
                <div className="font-mono text-xs text-[#6B7280] mt-1">{selected}</div>
              </div>
              <button onClick={() => { setSelected(null); setDetail(null); }} className="text-2xl text-[#6B7280] hover:text-[#2C3E50]">×</button>
            </div>

            <div className="grid grid-cols-3 gap-3 mb-6 p-4 bg-[#F7F9FB] rounded-2xl">
              <MiniStat label="Score" value={detail.page.quality.score} />
              <MiniStat label="Mots" value={detail.page.quality.word_count} />
              <MiniStat label="Liens" value={detail.page.quality.internal_link_count} />
            </div>
            {detail.page.quality.failed.length > 0 && (
              <div className="mb-6 p-4 bg-orange-50 border border-orange-200 rounded-2xl text-sm text-orange-800">
                <strong>Checks échoués :</strong> {detail.page.quality.failed.join(", ")}
              </div>
            )}

            <div className="space-y-4">
              <FormField label="Title (SEO)" value={form.title} onChange={(v) => setForm({ ...form, title: v })} testid="edit-title" />
              <FormField label="Meta description" value={form.meta_description} onChange={(v) => setForm({ ...form, meta_description: v })} testid="edit-meta" />
              <FormField label="H1" value={form.h1} onChange={(v) => setForm({ ...form, h1: v })} testid="edit-h1" />
              <div>
                <label className="block text-xs uppercase tracking-widest font-bold text-[#2C3E50] mb-2">Corps du texte (paragraphes séparés par ligne vide)</label>
                <textarea rows={12} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} className="input-square !rounded-2xl w-full text-sm" data-testid="edit-body" />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest font-bold text-[#2C3E50] mb-2">FAQ (JSON: [&#123;"q":"...", "a":"..."&#125;])</label>
                <textarea rows={6} value={form.faq} onChange={(e) => setForm({ ...form, faq: e.target.value })} className="input-square !rounded-2xl w-full font-mono text-xs" data-testid="edit-faq" />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest font-bold text-[#2C3E50] mb-2">Statut forcé (optionnel)</label>
                <select value={form.force_status} onChange={(e) => setForm({ ...form, force_status: e.target.value })} className="input-square w-full" data-testid="edit-status">
                  <option value="">(auto — selon quality gate)</option>
                  <option value="index">Forcer index</option>
                  <option value="noindex">Forcer noindex</option>
                  <option value="draft">Brouillon (masqué)</option>
                </select>
              </div>
            </div>

            <div className="mt-6 flex gap-3 justify-end">
              <button onClick={resetOverride} className="btn-outline !border-red-300 !text-red-700 hover:!bg-red-50" data-testid="reset-override">
                Supprimer les surcharges
              </button>
              <button onClick={save} disabled={saving} className="btn-primary disabled:opacity-50" data-testid="save-override">
                <Save className="w-4 h-4" /> {saving ? "Enregistrement…" : "Enregistrer"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const StatCard = ({ label, value, accent, testid }) => (
  <div
    className={`p-5 rounded-2xl border ${accent === "emerald" ? "bg-emerald-50 border-emerald-200" : accent === "orange" ? "bg-orange-50 border-orange-200" : "bg-white border-[#E5E7EB]"}`}
    data-testid={testid}
  >
    <div className="text-xs uppercase tracking-widest font-bold text-[#6B7280]">{label}</div>
    <div className="font-heading font-black text-4xl mt-1 text-[#2C3E50]">{value}</div>
  </div>
);

const MiniStat = ({ label, value }) => (
  <div className="text-center">
    <div className="text-xs uppercase tracking-widest text-[#6B7280]">{label}</div>
    <div className="font-heading font-extrabold text-2xl text-[#2C3E50]">{value}</div>
  </div>
);

const ScoreBadge = ({ score }) => {
  const color = score >= 90 ? "bg-emerald-100 text-emerald-700" : score >= 60 ? "bg-orange-100 text-orange-700" : "bg-red-100 text-red-700";
  return <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-bold ${color}`}>{score}</span>;
};

const FormField = ({ label, value, onChange, testid }) => (
  <div>
    <label className="block text-xs uppercase tracking-widest font-bold text-[#2C3E50] mb-2">{label}</label>
    <input value={value} onChange={(e) => onChange(e.target.value)} className="input-square w-full text-sm" data-testid={testid} />
  </div>
);

const SubmitPanel = ({ config, configForm, setConfigForm, saveConfig, savingConfig, submitIndexNow, submitting, submitResult, history, stats, cronStatus, triggerCron }) => {
  const siteUrl = window.location.origin;
  const indexNowKey = config?.indexnow_key || "";
  const keyFileUrl = indexNowKey ? `${siteUrl}/${indexNowKey}.txt` : "";
  const copy = (t) => navigator.clipboard?.writeText(t);

  return (
    <div className="space-y-8" data-testid="submit-panel">
      {/* ── Bloc IndexNow ── */}
      <section className="bg-white border border-[#1C1C1C] p-6 md:p-8">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div>
            <div className="eyebrow mb-1">Soumission automatique</div>
            <h2 className="font-heading font-extrabold text-2xl text-[#2C3E50]">IndexNow — Bing & Yandex</h2>
          </div>
        </div>
        <p className="text-sm text-[#6B7280] mb-4">
          IndexNow permet d'informer Bing, Yandex et Naver en temps réel des URLs modifiées ou nouvellement créées.
          Google n'est PAS supporté par IndexNow (voir bloc dédié plus bas).
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
          <button
            onClick={() => submitIndexNow("geo")}
            disabled={submitting || stats.index === 0}
            className="btn-primary disabled:opacity-50 !justify-start !text-left !px-5 !py-4 flex-col !items-start"
            data-testid="submit-indexnow-geo-btn"
          >
            <div className="flex items-center gap-2 w-full">
              <Radio className={`w-4 h-4 ${submitting ? "animate-pulse" : ""}`} />
              <span className="font-bold">Géo · Régions / Départements / Villes</span>
            </div>
            <span className="text-xs opacity-90 mt-1">{stats.index} URLs indexables</span>
          </button>

          <button
            onClick={() => submitIndexNow("intercity")}
            disabled={submitting}
            className="btn-primary disabled:opacity-50 !justify-start !text-left !px-5 !py-4 flex-col !items-start"
            data-testid="submit-indexnow-intercity-btn"
          >
            <div className="flex items-center gap-2 w-full">
              <Radio className={`w-4 h-4 ${submitting ? "animate-pulse" : ""}`} />
              <span className="font-bold">Intercités · Matrice 25×25</span>
            </div>
            <span className="text-xs opacity-90 mt-1">623 URLs (600 matrice + 23 courtes)</span>
          </button>

          <button
            onClick={() => submitIndexNow("blog")}
            disabled={submitting}
            className="btn-primary disabled:opacity-50 !justify-start !text-left !px-5 !py-4 flex-col !items-start"
            data-testid="submit-indexnow-blog-btn"
          >
            <div className="flex items-center gap-2 w-full">
              <Radio className={`w-4 h-4 ${submitting ? "animate-pulse" : ""}`} />
              <span className="font-bold">Blog</span>
            </div>
            <span className="text-xs opacity-90 mt-1">Articles + page d'index</span>
          </button>

          <button
            onClick={() => submitIndexNow("all")}
            disabled={submitting}
            className="disabled:opacity-50 !justify-start !text-left !px-5 !py-4 flex-col !items-start rounded-full font-bold text-sm uppercase tracking-wider transition-all bg-[#1A2A3A] text-white hover:bg-[#2C3E50] flex"
            data-testid="submit-indexnow-all-btn"
          >
            <div className="flex items-center gap-2 w-full">
              <Radio className={`w-4 h-4 ${submitting ? "animate-pulse" : ""}`} />
              <span className="font-bold">Tout soumettre</span>
            </div>
            <span className="text-xs opacity-90 mt-1 normal-case tracking-normal">Géo + Intercités + Blog en une passe</span>
          </button>
        </div>

        {/* ── Cron IndexNow quotidien ── */}
        {cronStatus && (
          <div className="mt-4 p-4 rounded-xl border border-[#E5E7EB] bg-gradient-to-br from-[#F7F9FB] to-white" data-testid="indexnow-cron-status">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex-1 min-w-[240px]">
                <div className="flex items-center gap-2 mb-1">
                  <span className={`w-2 h-2 rounded-full ${cronStatus.running ? "bg-[#2BD391]" : "bg-[#EF4444]"} animate-pulse`}></span>
                  <span className="text-xs font-bold uppercase tracking-wider text-[#2C3E50]">
                    Cron quotidien {cronStatus.running ? "actif" : "arrêté"}
                  </span>
                </div>
                <div className="text-sm text-[#2C3E50] font-semibold">{cronStatus.schedule}</div>
                {cronStatus.next_run && (
                  <div className="text-xs text-[#6B7280] mt-1">
                    Prochaine exécution : {new Date(cronStatus.next_run).toLocaleString("fr-FR", { dateStyle: "medium", timeStyle: "short" })}
                  </div>
                )}
                {cronStatus.last_run && (
                  <div className="text-xs text-[#6B7280] mt-1">
                    Dernière : {new Date(cronStatus.last_run.submitted_at).toLocaleString("fr-FR", { dateStyle: "medium", timeStyle: "short" })}
                    {" · "}{cronStatus.last_run.url_count} URLs
                    {" · "}
                    <span className={cronStatus.last_run.results?.yandex?.ok ? "text-[#1AA66C]" : "text-[#EF4444]"}>
                      Yandex {cronStatus.last_run.results?.yandex?.status || "?"}
                    </span>
                  </div>
                )}
              </div>
              <button
                onClick={triggerCron}
                disabled={submitting}
                className="btn-outline text-xs disabled:opacity-50"
                data-testid="trigger-cron-btn"
              >
                <Radio className="w-3.5 h-3.5" />
                Déclencher maintenant
              </button>
            </div>
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm mb-4">
          <div className="p-4 bg-[#F7F9FB] rounded-xl">
            <div className="text-xs uppercase tracking-widest text-[#6B7280] mb-1 font-bold">Clé publique IndexNow</div>
            <div className="font-mono text-xs text-[#2C3E50] break-all">{indexNowKey}</div>
          </div>
          <div className="p-4 bg-[#F7F9FB] rounded-xl">
            <div className="text-xs uppercase tracking-widest text-[#6B7280] mb-1 font-bold">Fichier de vérification</div>
            <a href={keyFileUrl} target="_blank" rel="noreferrer" className="font-mono text-xs text-[#1AA66C] hover:underline break-all">
              {keyFileUrl} <ExternalLink className="w-3 h-3 inline" />
            </a>
          </div>
        </div>

        {submitResult && (
          <div className="p-4 bg-[#F7F9FB] rounded-xl border border-[#E5E7EB] text-sm" data-testid="submit-result">
            <div className="font-bold mb-2 text-[#2C3E50]">
              Résultat — {submitResult.url_count} URLs
              {submitResult.scope && <span className="ml-2 text-xs font-normal text-[#6B7280]">· scope : {submitResult.scope}</span>}
            </div>
            <ul className="space-y-1">
              {Object.entries(submitResult.results || {}).map(([name, r]) => (
                <li key={name} className="flex items-center gap-2">
                  {r.ok ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <XCircle className="w-4 h-4 text-red-600" />}
                  <span className="font-semibold uppercase text-xs w-16">{name}</span>
                  <span className="font-mono text-xs">HTTP {r.status}</span>
                  {r.error && <span className="text-xs text-red-600 truncate">— {r.error}</span>}
                </li>
              ))}
            </ul>
            {!submitResult.ok && submitResult.error && (
              <div className="mt-2 text-xs text-red-600">{submitResult.error}</div>
            )}
          </div>
        )}
      </section>

      {/* ── Bloc Google Search Console ── */}
      <section className="bg-white border border-[#1C1C1C] p-6 md:p-8">
        <div className="eyebrow mb-1">Google</div>
        <h2 className="font-heading font-extrabold text-2xl text-[#2C3E50] mb-3">Google Search Console</h2>
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl text-sm text-blue-900 mb-5">
          <Info className="w-4 h-4 inline mr-1.5" />
          <b>Google n'accepte plus la soumission automatique de sitemaps depuis juin 2023</b> (l'ancien endpoint <code>/ping</code> est déprécié).
          La seule voie officielle est l'ajout manuel du sitemap dans Google Search Console.
        </div>
        <ol className="space-y-4 text-sm text-[#2C3E50]">
          <li>
            <b>1. Vérifier la propriété du site :</b><br/>
            Dans <a href="https://search.google.com/search-console" target="_blank" rel="noreferrer" className="text-[#1AA66C] hover:underline">Search Console</a>, ajoutez la propriété <code>https://demenageurpro.fr</code>, choisissez la vérification par balise HTML et collez le code obtenu ci-dessous.
          </li>
          <li>
            <FormField
              label="Code de vérification Google (attribut content=)"
              value={configForm.google_site_verification}
              onChange={(v) => setConfigForm({ ...configForm, google_site_verification: v })}
              testid="input-google-verification"
            />
          </li>
          <li>
            <b>2. Soumettre les sitemaps :</b><br/>
            Dans GSC → <i>Sitemaps</i>, ajoutez ces URLs :
            <div className="mt-2 space-y-1">
              {["sitemap.xml", "sitemap-regions.xml", "sitemap-departements.xml", "sitemap-villes.xml", "sitemap-main.xml"].map((s) => (
                <div key={s} className="flex items-center gap-2 bg-[#F7F9FB] rounded-lg px-3 py-2 font-mono text-xs">
                  <code className="flex-1 break-all">{siteUrl}/api/{s}</code>
                  <button onClick={() => copy(`${siteUrl}/api/${s}`)} className="text-[#1AA66C] hover:underline">
                    <Copy className="w-3 h-3 inline" /> Copier
                  </button>
                </div>
              ))}
            </div>
          </li>
        </ol>
      </section>

      {/* ── Bloc Bing Webmaster ── */}
      <section className="bg-white border border-[#1C1C1C] p-6 md:p-8">
        <div className="eyebrow mb-1">Bing</div>
        <h2 className="font-heading font-extrabold text-2xl text-[#2C3E50] mb-3">Bing Webmaster Tools</h2>
        <p className="text-sm text-[#6B7280] mb-4">
          Bing utilise IndexNow (soumission automatique ci-dessus). Ajoutez également votre site dans <a href="https://www.bing.com/webmasters" target="_blank" rel="noreferrer" className="text-[#1AA66C] hover:underline">Bing Webmaster Tools</a> pour suivre les statistiques.
        </p>
        <FormField
          label="Code de vérification Bing (msvalidate.01)"
          value={configForm.bing_verification}
          onChange={(v) => setConfigForm({ ...configForm, bing_verification: v })}
          testid="input-bing-verification"
        />
      </section>

      {/* Bouton save */}
      <div className="flex justify-end">
        <button onClick={saveConfig} disabled={savingConfig} className="btn-primary disabled:opacity-50" data-testid="save-seo-config">
          <Save className="w-4 h-4" /> {savingConfig ? "Enregistrement…" : "Enregistrer les codes de vérification"}
        </button>
      </div>

      {/* Historique */}
      {history.length > 0 && (
        <section className="bg-white border border-[#1C1C1C] p-6">
          <h3 className="font-heading font-extrabold text-lg text-[#2C3E50] mb-3">Historique des soumissions IndexNow</h3>
          <div className="space-y-2 text-sm">
            {history.map((h) => (
              <div key={h.id} className="flex items-center justify-between p-3 bg-[#F7F9FB] rounded-lg" data-testid={`history-${h.id}`}>
                <div>
                  <div className="text-xs text-[#6B7280]">{new Date(h.submitted_at).toLocaleString("fr-FR")}</div>
                  <div className="font-semibold">
                    {h.url_count} URLs
                    {h.scope && <span className="ml-2 text-xs font-normal text-[#6B7280]">· {h.scope}</span>}
                  </div>
                </div>
                <div className="flex gap-2 text-xs">
                  {Object.entries(h.results || {}).map(([n, r]) => (
                    <span key={n} className={`px-2 py-1 rounded-full ${r.ok ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>
                      {n} {r.status}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
};
